import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, change } from 'redux-form';
import { t } from 'i18next';
import { isRequired, maxAddressLine1, maxChar100 } from '../../../utils/validator';
import zipPlus4ToZip from './helpers';
import { capitalizeFirstLetter } from '../../../utils/func';
import AddressInput from '../../../lib/ui/form_elements/AddressInput';
import TextInput from '../../../lib/ui/form_elements/TextInput';

const loadScriptAsync = function loadScriptAsync(uri, elementID) {
  return new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.id = elementID;
    tag.src = uri;
    tag.async = true;
    tag.onload = () => {
      resolve();
    };

    tag.onerror = () => {
      document.getElementById(elementID).parentNode.removeChild(tag);
    };

    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  });
};

const loadStyleSheetAsync = function loadStyleSheetAsync(href, elementID) {
  return new Promise((resolve) => {
    const tag = document.createElement('link');
    tag.id = elementID;
    tag.rel = 'styleSheet';
    tag.href = href;
    tag.type = 'text/css';
    tag.async = true;
    tag.onload = () => {
      resolve();
    };

    tag.onerror = () => {
      document.getElementById(elementID).parentNode.removeChild(tag);
      // loadStyleSheetAsync(href, elementID)
    };

    const firstLinkTag = document.getElementsByTagName('link')[0];
    firstLinkTag.parentNode.insertBefore(tag, firstLinkTag);
  });
};

class AddressComplete extends PureComponent {
  componentDidMount() {
    const { isIncomplete, isFromQuote } = this.props;
    // Also check URL directly — isFromQuote prop may not be set yet when mounting
    // because quoteOrShipmentNumber is populated asynchronously from redux form
    const urlParams = typeof window !== 'undefined' && new URLSearchParams(window.location.search);
    const quoteFromUrl = urlParams && urlParams.get('quoteNumber');
    const isFromQuoteUrl = !!(quoteFromUrl && quoteFromUrl.toString().toUpperCase().includes('Q'));
    if (!isIncomplete && !isFromQuote && !isFromQuoteUrl) {
      this.addressApiInit();
    }
  }

  addressApiInit = async () => {
    if (!global.CPscriptLoaded) {
      global.CPscriptLoaded = loadScriptAsync('https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js', 'CPScript');
    }

    global.CPscriptLoaded.then(() => {
      if (!global.CPstyleLoaded) {
        global.CPstyleLoaded = loadStyleSheetAsync('https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css', 'CPStyle');
      }

      return global.CPstyleLoaded;
    }).then(() => {
      const {
        dispatch, formName, fieldName, contactType, basedOnAQuoteOrShipment,
      } = this.props;

      const shipperAddressFields = [
        { element: fieldName, field: '', mode: global.pca.fieldMode.SEARCH },
      ];
      const options = {
        key: t('CanadaPostAddressCompleteApiKey'),
        countries: { codesList: 'CAN, USA' },
      };

      // Decorate field whose "name" and / or "id" matches shipperAddressFields elements' "element" attribute...
      const addressElement = new global.pca.Address(shipperAddressFields, options);

      addressElement.listen('search', (obj) => {
        const { postalCodeId } = this.props;
        if (postalCodeId) {
          obj.lastId = postalCodeId;
        }
      });

      // Filter results when searching addresses of a specific postal code
      addressElement.listen('display', () => {
        const { isIncomplete } = this.props;

        const fieldId = addressElement.fields[0].element;
        const { value } = document.getElementById(fieldId);

        const list = document.getElementsByClassName('pcalist');
        const items = document.getElementsByClassName('pcaitem');

        if (items) {
          [...items].forEach((item) => {
            const match = (item.title.indexOf(value) > -1);
            if (match || !isIncomplete) {
              item.style.display = 'block';
            } else {
              item.style.display = 'none';
            }
          });
        }

        if (list && list.length > 0) {
          list[0].style.maxHeight = '217px';
          list[0].style.height = 'auto';
        }
      });

      // addressElement.onChange() =>
      addressElement.listen('populate', async (address) => {
        // Formatting pieces that will make up the field value, given country, ...
        let postalCode;
        let countryName;
        if (address.CountryIso2 === 'US') {
          postalCode = zipPlus4ToZip(address.PostalCode);
          countryName = address.CountryIso3;
        } else {
          postalCode = address.PostalCode;
          countryName = address.CountryName;
        }

        // Format field value...
        const formattedAddress = `${address.Line1}, ${address.City}, ${address.ProvinceCode} ${postalCode}, ${countryName}`;

        // Update redux form field value...
        if (!basedOnAQuoteOrShipment) {
          dispatch(change(formName, `${contactType}Address`, formattedAddress));
          dispatch(change(formName, `${contactType}AddressLine1`, address.Line1));
          dispatch(change(formName, `${contactType}City`, address.City));
          dispatch(change(formName, `${contactType}ProvinceState`, address.ProvinceCode));
          dispatch(change(formName, `${contactType}PostalZipCode`, postalCode));
          dispatch(change(formName, `${contactType}Country`, countryName));
          dispatch(change(formName, `${contactType}CountryCode`, address.CountryIso2));

          dispatch(change(formName, `is${capitalizeFirstLetter(contactType)}AddressCanadaPost`, true));
        }
      });
    });
  };

  handleChange = async () => {
    const {
      // eslint-disable-next-line no-unused-vars
      basedOnAQuoteOrShipment, avoidChanges, dispatch, formName, contactType, fieldName,
    } = this.props;

    if (basedOnAQuoteOrShipment) {
      avoidChanges(fieldName);
    }

    if (!basedOnAQuoteOrShipment) {
      dispatch(change(formName, `is${capitalizeFirstLetter(contactType)}AddressCanadaPost`, false));
    }
  };

  render() {
    const {
      fieldName,
      label,
      required,
      basedOnAQuoteOrShipment,
      avoidChanges,
      isIncomplete,
      isFromQuote,
    } = this.props;

    // Also check URL directly — isFromQuote prop may not be set yet on first render
    // because quoteOrShipmentNumber is populated asynchronously from redux form
    const urlParams = typeof window !== 'undefined' && new URLSearchParams(window.location.search);
    const quoteFromUrl = urlParams && urlParams.get('quoteNumber');
    const isFromQuoteResolved = isFromQuote || !!(quoteFromUrl && quoteFromUrl.toString().toUpperCase().includes('Q'));

    const isOptionalField = !required
      ? [maxChar100, maxAddressLine1]
      : [isRequired, maxChar100, maxAddressLine1];

    // When address is incomplete (address1 null from API), render plain text input
    if (isIncomplete) {
      return (
        <Field
          name={fieldName}
          label={label || t('Address')}
          component={TextInput}
          validate={isOptionalField}
          maxLength={100}
          optional={!required}
        />
      );
    }

    // When creating from a quote, render an editable text input without the address dropdown.
    // Also sync the individual city field on every keystroke, since the API (services/orders)
    // reads city from shipperCity/consigneeCity directly — not from parsing the combined string.
    if (isFromQuoteResolved) {
      const { contactType: ct, dispatch: d, formName: fn } = this.props;
      return (
        <Field
          name={fieldName}
          label={label || t('Address')}
          component={TextInput}
          validate={isOptionalField}
          maxLength={100}
          optional={!required}
          onChange={(e, newValue) => {
            if (ct && d && fn) {
              const parts = newValue.split(',');
              if (parts.length >= 3) {
                const parsedCity = parts[parts.length - 3].trim();
                d(change(fn, `${ct}City`, parsedCity));
              }
            }
          }}
        />
      );
    }

    return (
      <Field
        id={fieldName}
        name={fieldName}
        label={label || t('Address')}
        component={AddressInput}
        validate={isOptionalField}
        onChange={this.handleChange}
        avoidChanges={(basedOnAQuoteOrShipment) ? avoidChanges : null}
        readOnly={basedOnAQuoteOrShipment}
        maxLength={100}
        optional={!required}
      />
    );
  }
}

AddressComplete.propTypes = {
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  contactType: PropTypes.string,
  fieldName: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  avoidChanges: PropTypes.func,
  basedOnAQuoteOrShipment: PropTypes.bool,
  isIncomplete: PropTypes.bool,
  postalCodeId: PropTypes.string,
  isFromQuote: PropTypes.bool,
};

export default connect()(AddressComplete);
